import { useEffect, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { closestCenter, DndContext, MouseSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { restrictToVerticalAxis, restrictToWindowEdges } from '@dnd-kit/modifiers';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Box, Button, Checkbox, dialogContentClasses, Icon, IconButton, paperClasses, Tooltip, Typography } from '@mui/material';

import Dialog from 'Lib/muiComponents/dialog/Dialog';
import isEmpty from 'Lib/utilities/isEmpty';
import reorder from 'Lib/utilities/reorder';
import { screenSize } from 'Lib/utilities/screenSize';

const SortableItem = ({ header, id, handleSelect }) => {
	const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
		id: id,
		transition: {
			duration: 150,
			easing: 'cubic-bezier(0.25, 1, 0.5, 1a)'
		}
	});

	return (
		<Box
			sx={{
				py: 1,
				pl: 1,
				pr: 4,
				mb: 2,
				borderRadius: 2,
				display: 'flex',
				justifyContent: 'space-between',
				alignItems: 'center',
				border: theme => `1px solid ${theme.palette.common.gray}`,
				transform: CSS.Transform.toString(transform),
				transition,
				zIndex: isDragging ? '100' : 'auto',
				opacity: isDragging ? 0.3 : 1
			}}
			ref={setNodeRef}>
			<Box sx={{ display: 'flex', alignItems: 'center' }}>
				<Checkbox color="success" key={header.visible} checked={header.visible} onClick={event => handleSelect(event, header)} />
				<Typography sx={{ ml: 1, width: '85%' }} noWrap>
					{header.label}
				</Typography>
			</Box>
			<Box>
				<Icon {...attributes} {...listeners}>
					drag_handle
				</Icon>
			</Box>
		</Box>
	);
};

const ColumnSettings = ({ actions, open, toggleModal }) => {
	const dispatch = useDispatch();
	const columns = useSelector(state => state.ui.table.columns, shallowEqual);
	const rows = useSelector(state => state.ui.table.rows, shallowEqual);

	const [updatedColumns, setUpdatedColumns] = useState([]);
	const [columnLabels, setColumnLabels] = useState([]);
	const [disableUpdate, setDisableUpdate] = useState(true);

	useEffect(() => {
		setUpdatedColumns(columns);
		setColumnLabels(columns.map(column => column.order));
	}, [columns]);

	const onDragEnd = event => {
		const { active, over } = event;

		if (active.id !== over.id) {
			setUpdatedColumns(items => {
				const oldIndex = items.findIndex(items => items.order === active.id);
				const newIndex = items.findIndex(items => items.order === over.id);

				return reorder(items, oldIndex, newIndex);
			});
		}
		setDisableUpdate(false);
	};

	const handleSelect = (event, header) => {
		setUpdatedColumns(prevState =>
			prevState.map(item =>
				item.label == header.label
					? {
							...item,
							visible: event.target.checked
						}
					: item
			)
		);
		setDisableUpdate(false);
	};

	const handleUpdate = () => {
		setDisableUpdate(true);
		updateTable(updatedColumns);
		toggleModal();
	};

	const updateTable = list => {
		const orderMap = new Map(list.map(({ order }, index) => [order, index + 1]));
		const visibilityMap = new Map(list.map(({ order, visible }) => [order, visible]));

		const updateList = originalList =>
			originalList
				.map(item => ({
					...item,
					order: orderMap.get(item.order),
					visible: visibilityMap.get(item.order)
				}))
				.sort((a, b) => a.order - b.order);

		dispatch(
			actions.setTableInformation({
				columns: updateList(columns),
				rows: updateList(rows)
			})
		);
	};

	const handleToggle = () => {
		toggleModal();
		setUpdatedColumns(columns);
		setDisableUpdate(true);
	};

	const sensors = useSensors(
		useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
		useSensor(TouchSensor, {
			activationConstraint: {
				delay: 250,
				tolerance: 5
			}
		})
	);

	return (
		<Dialog
			open={open}
			title="Customize Columns"
			toggle={handleToggle}
			actions={() => (
				<Box>
					<Button variant="secondary" onClick={handleToggle} sx={{ mr: 2 }}>
						Cancel
					</Button>
					<Button variant="primary" type="submit" onClick={handleUpdate} disabled={disableUpdate}>
						Update
					</Button>
				</Box>
			)}
			sx={{
				[`& .${paperClasses.root}`]: {
					width: { sm: '400px' },
					[`& .${dialogContentClasses.root}`]: { pt: 3 }
				}
			}}>
			<DndContext collisionDetection={closestCenter} onDragEnd={onDragEnd} sensors={sensors} modifiers={[restrictToWindowEdges, restrictToVerticalAxis]}>
				<Box>
					<SortableContext items={columnLabels} strategy={verticalListSortingStrategy}>
						{updatedColumns.map(header => (
							<SortableItem key={header.label} id={header.order} header={header} handleSelect={handleSelect} />
						))}
					</SortableContext>
				</Box>
			</DndContext>
		</Dialog>
	);
};

const MobileSettings = ({ sx, toggleOpen }) => (
	<IconButton aria-label="open table settings" sx={sx} onClick={toggleOpen}>
		<Icon>view_week</Icon>
	</IconButton>
);

export default function Settings({ actions, cardTable, sx }) {
	const { columns } = useSelector(state => state.ui.table);

	const [open, setOpen] = useState(false);

	const { mobile } = screenSize();

	const toggleOpen = () => setOpen(prevState => !prevState);

	if (isEmpty(columns)) return null;

	return (
		<>
			{mobile ? (
				<MobileSettings sx={sx} toggleOpen={toggleOpen} />
			) : (
				<Tooltip arrow data-cy="table-settings-tooltip" title="Customize Columns" placement="bottom">
					<Box sx={sx}>
						<IconButton data-cy="table-settings-button" variant={cardTable ? 'noBorder' : 'regular'} onClick={toggleOpen}>
							<Icon>view_week</Icon>
						</IconButton>
					</Box>
				</Tooltip>
			)}
			<ColumnSettings actions={actions} open={open} toggleModal={toggleOpen} />
		</>
	);
}
